import { parsePrice, calculateTripCost } from './statsUtils';

/* Process the categories into price breakdown */
export const buildCategoryBreakdown = (trip) => {

    // One entry per tag that actually has money
    const buckets = {};
    // Price-but-no-tag money
    let uncategorized = 0;
    // Did ANY day have the category column?
    let hasCategoryColumn = false;

    (trip.days || []).forEach(day => {

        // Does THIS day have the tag category column? (per-day, because each day owns its columns)
        const catCol = (day.columns || []).find(c => c.id === 'category' && c.type === 'tags');
        if (catCol) hasCategoryColumn = true;

        (day.rows || []).forEach(row => {
            const amount = parsePrice(row.price);
            // No money → nothing to bucket
            if (!amount) return;

            // Get the FIRST tag from row.category
            const tag = (row.category || [])[0];

            if (!tag) {
                // This is untagged money — add to uncategorized total
                uncategorized += amount;
            } else {
                // If no bucket for this tag yet, create one
                if (!buckets[tag]) buckets[tag] = { total: 0, items: [] };
                // Add `amount` to this bucket's total
                buckets[tag].total += amount;
                // Push { place, price } to its items
                buckets[tag].items.push({
                    place: row.place || '(Untitled)',
                    price: amount
                });
            }
        });
    });

    const total = calculateTripCost(trip);

    return { hasCategoryColumn, total, uncategorized, buckets };
};

/* Find the single most-expensive category across ALL trips */
export const getMostExpensiveCategory = (trips) => {
    const totals = {};

    trips.forEach(trip => {
        const { buckets } = buildCategoryBreakdown(trip);   // reuse the per-trip util
        Object.entries(buckets).forEach(([name, data]) => {
            totals[name] = (totals[name] || 0) + data.total;
        });
    });

    // Pick the winner
    let topCategory = '-';
    let topTotal = 0;
    Object.entries(totals).forEach(([name, total]) => {
        if (total > topTotal) {
            topCategory = name;
            topTotal = total;
        }
    });

    return { category: topCategory, total: topTotal };
};

/* =========================================================== */
/* ==== EXPENSE SETTLEMENT (who paid how much / who owes) ==== */
/* =========================================================== */

/* One member's share of a single price-split expense */
export const getMemberShare = (splitValue, userId, effectiveAmount) => {
    const customSplits = splitValue.customSplits || {};
    const splitWith = splitValue.splitWith || [];

    if (splitValue.splitOption === 'exact') {
        return parseFloat(customSplits[userId]) || 0;
    }
    if (splitValue.splitOption === 'percentage') {
        return (parseFloat(customSplits[userId]) || 0) / 100 * effectiveAmount;
    }
    // 'equal' (default)
    return splitWith.length > 0 ? effectiveAmount / splitWith.length : 0;
};

/*
 * The effective total of a price-split cell. 
 * Mirrors ItineraryBoard's linkedPrice logic: when the row has a Price (number) column, that is the source of truth.
 * Otherwise fall back to the amount stored on the split value itself.
 */
export const getRowEffectiveAmount = (day, row, splitValue) => {
    const columns = day.columns || [];
    const priceCol = columns.find(c => c.id === 'price' && c.type === 'number')
        || columns.find(c => c.type === 'number');

    if (priceCol) return parsePrice(row[priceCol.id]);
    return splitValue.amount ?? 0;
};

/* Round to cents, keeping money math free of float dust */
const round2 = (x) => Math.round(x * 100) / 100;

/*
 * Minimize the number of payments needed to settle everyone up.
 *
 * Works purely from each person's net position (paid − share), which lets money
 * flow transitively: if A owes B and B owes C, A can pay C directly. It repeatedly
 * settles the biggest creditor against the biggest debtor; each step fully clears
 * at least one person, so it finishes in at most n-1 transactions. This is the
 * standard "simplify debts" heuristic — always correct, and near-minimal in practice
 * (the provably-minimal version is NP-hard, so a heuristic is the norm).
 *
 * Example: A -50, B -10, C +60  →  A pays C $50, B pays C $10  (2 payments).
 */
const simplifyDebts = (perPerson) => {
    // Signed net balances, dropping anyone already settled
    const balances = Object.entries(perPerson)
        .map(([id, p]) => ({ id, amount: round2(p.net) }))
        .filter(b => Math.abs(b.amount) >= 0.005);

    const transactions = [];

    while (true) {
        let creditor = null;   // most owed money (largest positive net)
        let debtor = null;     // owes the most (most negative net)
        for (const b of balances) {
            if (b.amount >= 0.005 && (!creditor || b.amount > creditor.amount)) creditor = b;
            if (b.amount <= -0.005 && (!debtor || b.amount < debtor.amount)) debtor = b;
        }
        if (!creditor || !debtor) break;

        const settled = round2(Math.min(creditor.amount, -debtor.amount));
        transactions.push({ from: debtor.id, to: creditor.id, amount: settled });
        creditor.amount = round2(creditor.amount - settled);
        debtor.amount = round2(debtor.amount + settled);
    }

    return transactions;
};

/*
 * Expense settlement across a whole trip.
 * Only price-split cells contribute.
 * Each person's paid/share/net is accumulated, then the debts are simplified into
 * the fewest payments (money may flow transitively — see simplifyDebts).
 *
 * Returns:
 *   hasSplitData    — did any usable price-split expense exist?
 *   perPerson       — { [userId]: { paid, share, net } } ("who paid how much")
 *   transactions    — [ { from, to, amount } ]           ("who owes whom", minimized)
 *   unassignedCount — priced expenses skipped because they had no payer
 * All ids are user_ids; the UI maps them to member name/avatar via trip.members.
 */
export const buildSettlement = (trip) => {
    const perPerson = {};   // { [userId]: { paid, share, net } }
    let unassignedCount = 0;
    let hasSplitData = false;

    const ensurePerson = (id) => {
        if (!perPerson[id]) perPerson[id] = { paid: 0, share: 0, net: 0 };
        return perPerson[id];
    };

    (trip.days || []).forEach(day => {
        // Columns are per-day, so find this day's price-split columns
        const psCols = (day.columns || []).filter(c => c.type === 'price-split');
        if (psCols.length === 0) return;

        (day.rows || []).forEach(row => {
            psCols.forEach(col => {
                const value = row[col.id];
                if (!value || typeof value !== 'object') return;

                const amount = getRowEffectiveAmount(day, row, value);

                // Can't settle an expense with no payer — flag it and skip
                if (!value.paidBy) {
                    if (amount > 0) unassignedCount++;
                    return;
                }

                const participants = value.splitWith || [];
                if (amount <= 0 || participants.length === 0) return;
                hasSplitData = true;

                // Tally each participant's share and the payer's out-of-pocket total
                let cellTotal = 0;
                participants.forEach(u => {
                    const share = getMemberShare(value, u, amount);
                    cellTotal += share;
                    ensurePerson(u).share += share;
                });
                // The payer fronted the whole expense
                ensurePerson(value.paidBy).paid += cellTotal;
            });
        });
    });

    // Round the accumulated figures and derive each person's net position
    Object.values(perPerson).forEach(p => {
        p.paid = round2(p.paid);
        p.share = round2(p.share);
        p.net = round2(p.paid - p.share);
    });

    return {
        hasSplitData,
        perPerson,
        transactions: simplifyDebts(perPerson),
        unassignedCount,
    };
};